'use client';

import { Dialog as BaseDialog } from '@base-ui-components/react/dialog';
import { X } from 'lucide-react';

import { cn } from '../../utils/cn';
import { useInheritedColorScheme } from '../../utils/use-inherited-color-scheme';
import './dialog.css';

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function Dialog({ children, ...props }: DialogProps) {
  return <BaseDialog.Root {...props}>{children}</BaseDialog.Root>;
}

interface DialogTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function DialogTrigger({ className, ...props }: DialogTriggerProps) {
  return (
    <BaseDialog.Trigger
      className={cn(className)}
      {...(props as React.ComponentProps<typeof BaseDialog.Trigger>)}
    />
  );
}

interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  closeLabel?: string;
}

function DialogContent({
  className,
  children,
  closeLabel = 'Close',
  ...props
}: DialogContentProps) {
  const { probeRef, themeAttrs } = useInheritedColorScheme();

  return (
    <>
      <span ref={probeRef} style={{ display: 'none' }} />
      <BaseDialog.Portal>
        <BaseDialog.Backdrop
          className={cn(
            'dialog-backdrop fixed inset-0 z-50 bg-black/80',
            'transition-opacity duration-150',
            'data-[starting-style]:opacity-0',
            'data-[ending-style]:opacity-0'
          )}
        />
        <BaseDialog.Popup
          {...themeAttrs}
          className={cn(
            'bg-background p-card fixed top-1/2 left-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border shadow-lg',
            'rounded-lg',
            'transition-all duration-150',
            'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
            'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className
          )}
          {...(props as React.ComponentProps<typeof BaseDialog.Popup>)}
        >
          {children}
          <BaseDialog.Close
            className="ring-offset-background focus-visible:ring-ring absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none"
            aria-label={closeLabel}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">{closeLabel}</span>
          </BaseDialog.Close>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </>
  );
}

function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'gap-tight flex flex-col text-center sm:text-left',
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'sm:gap-item flex flex-col-reverse sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <BaseDialog.Title
      className={cn(
        'text-lg leading-none font-semibold tracking-tight',
        className
      )}
      {...(props as React.ComponentProps<typeof BaseDialog.Title>)}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <BaseDialog.Description
      className={cn('text-muted-foreground text-sm', className)}
      {...(props as React.ComponentProps<typeof BaseDialog.Description>)}
    />
  );
}

function DialogClose({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <BaseDialog.Close
      className={cn(className)}
      {...(props as React.ComponentProps<typeof BaseDialog.Close>)}
    />
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
export type { DialogProps, DialogTriggerProps, DialogContentProps };
